import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon } from 'lucide-react';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};
export function ProblemSection() {
  return <section className="px-4 py-20 md:py-28" data-id="element-369">
      <div className="max-w-3xl mx-auto" data-id="element-370">
        {/* The Problem */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-371">
          <div className="border-t-2 border-rescue-gold/40 w-16 mb-6" aria-hidden="true" data-id="element-372" />
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-373">
            The Problem
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        delay: 0.1,
        ease: 'easeOut'
      }} className="space-y-5 mb-16" data-id="element-374">
          <p className="font-body text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-375">
            You aren't "bad at money." You are caught in a system{' '}
            <span className="text-rescue-gold font-semibold" data-id="element-376">
              designed to confuse you
            </span>{' '}
            into paying for "features" you don't need. The tax prep industry
            spends millions to hide the free government tools you are legally
            entitled to use.
          </p>
          <p className="font-body text-base md:text-lg text-gray-300 leading-relaxed" data-id="element-377">
            They want you to believe filing your own taxes is a dark art that
            requires a $400-an-hour accountant or a bloated software giant. They
            use{' '}
            <span className="text-rescue-gold font-semibold" data-id="element-378">
              "Audit Protection"
            </span>{' '}
            as a scare tactic to sell you peace of mind that should already be
            yours for free. If you don't have the cash to pay a corporation to
            tell the government how much money you made, you feel like a
            failure.
          </p>
        </motion.div>

        {/* Why What You Tried Didn't Work */}
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} data-id="element-379">
          <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-8" data-id="element-380">
            Why What You Tried Didn't Work
          </h3>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-40px'
      }} className="space-y-4" data-id="element-381">
          <motion.div variants={itemVariants} className="bg-midnight-800 border border-midnight-700/60 rounded-lg p-6" data-id="element-382">
            <div className="flex items-start gap-4" data-id="element-383">
              <div className="flex-shrink-0 mt-0.5" data-id="element-384">
                <AlertTriangleIcon className="w-5 h-5 text-rescue-red" aria-hidden="true" data-id="element-385" />
              </div>
              <div data-id="element-386">
                <h4 className="font-body text-lg font-bold text-white mb-2" data-id="element-387">
                  The "Free Edition" Trap
                </h4>
                <p className="font-body text-gray-400 leading-relaxed" data-id="element-388">
                  It's only free until you add a single 1099 or a basic
                  deduction, then the price jumps to $60, $90, or $120
                  instantly.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-midnight-800 border border-midnight-700/60 rounded-lg p-6" data-id="element-389">
            <div className="flex items-start gap-4" data-id="element-390">
              <div className="flex-shrink-0 mt-0.5" data-id="element-391">
                <AlertTriangleIcon className="w-5 h-5 text-rescue-red" aria-hidden="true" data-id="element-392" />
              </div>
              <div data-id="element-393">
                <h4 className="font-body text-lg font-bold text-white mb-2" data-id="element-394">
                  Artificial Complexity
                </h4>
                <p className="font-body text-gray-400 leading-relaxed" data-id="element-395">
                  They use jargon to make simple questions feel like trap doors,
                  forcing you to pay for "expert help."
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-midnight-800 border border-midnight-700/60 rounded-lg p-6" data-id="element-396">
            <div className="flex items-start gap-4" data-id="element-397">
              <div className="flex-shrink-0 mt-0.5" data-id="element-398">
                <AlertTriangleIcon className="w-5 h-5 text-rescue-red" aria-hidden="true" data-id="element-399" />
              </div>
              <div data-id="element-400">
                <h4 className="font-body text-lg font-bold text-white mb-2" data-id="element-401">
                  The Sunk Cost Shakedown
                </h4>
                <p className="font-body text-gray-400 leading-relaxed" data-id="element-402">
                  They wait until the deadline is hours away to reveal the
                  price, knowing you're too tired to start over elsewhere.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="bg-midnight-800 border border-midnight-700/60 rounded-lg p-6" data-id="element-403">
            <div className="flex items-start gap-4" data-id="element-404">
              <div className="flex-shrink-0 mt-0.5" data-id="element-405">
                <AlertTriangleIcon className="w-5 h-5 text-rescue-red" aria-hidden="true" data-id="element-406" />
              </div>
              <div data-id="element-407">
                <h4 className="font-body text-lg font-bold text-white mb-2" data-id="element-408">
                  Search Engine Sabotage
                </h4>
                <p className="font-body text-gray-400 leading-relaxed" data-id="element-409">
                  When you Google "Free File," the first four results are paid
                  ads disguised as government portals.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}